import styled from 'styled-components';
import { FormControl } from "@mui/material";

export const Page = styled.div`
    height: 100%;
    background-color: white;
    overflow: auto;
    font-family: var(--fontMain, Poppins), sans-serif;
`;

export const PageContainer = styled(Page)`
    width: 100%;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 80px;
    box-sizing: border-box;

    @media (min-width: 1200px) {
        max-width: 900px;
        padding-left: 2rem;
        padding-right: 2rem;
    }

    .headerRow {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        padding: 1rem 0;
        margin-bottom: 0.5rem;
    }
    
    .backButton {
        position: absolute;
        left: 0;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        background: transparent;
        border: 1px solid #E0E0E0;
        border-radius: 8px;
        color: var(--primary-blue, #0B4598);
        font-family: var(--fontMain, Poppins), sans-serif;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        
        svg {
            font-size: 1.25rem;
        }
        
        &:hover {
            background: rgba(11, 69, 152, 0.06);
            border-color: var(--primary-blue, #0B4598);
        }
        
        &:active {
            background: rgba(11, 69, 152, 0.1);
        }
    }

    .pageTitle {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
        color: rgba(0, 0, 0, 0.87);
    }
`;

export const SectionContainer = styled.div<{ isPaymentDetails?: boolean }>`
    background-color: ${props => props.isPaymentDetails ? '#F8F9FA' : 'transparent'};
    border-radius: ${props => props.isPaymentDetails ? '0.5rem' : '0'};
    padding: 0.5rem;
    margin-bottom: ${props => props.isPaymentDetails ? '2rem' : '0'};
    h2 {
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: rgba(0, 0, 0, 0.87);
    }

    label {
        color: rgba(0, 0, 0, 0.6);
        font-size: 0.875rem;
        margin-bottom: 0.5rem;
        display: block;
    }
`;

export const TalentIdentity = styled.div`
    background: #F8FAFC;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;

    .label {
        color: #64748B;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        margin-bottom: 4px;
        text-transform: uppercase;
    }

    .name {
        color: #0F172A;
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.5;
    }
`;

export const TimeInputField = styled.div`
    margin-bottom: 1rem;

    .invoiceTimeHelper {
        margin-top: 0.375rem;
        color: #6D6D72;
        font-size: 0.75rem;
        line-height: 1.45;
    }
    
    .MuiOutlinedInput-root {
        background-color: white;
        border-radius: 4px;
        height: 48px;
    }

    .MuiInputLabel-root {
        color: rgba(0, 0, 0, 0.6);
        font-size: 0.875rem;
    }

    .MuiSelect-select {
        padding: 8px 14px;
    }
`;

export const BillingRow = styled('div')<{ isTotalAmount?: boolean }>`
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    color: rgba(0, 0, 0);
    font-size: 0.875rem;
    line-height: 1.5;
    &.billable-duration-row {
        gap: 12px;
        align-items: flex-start;
        > div:first-child { flex-shrink: 0; }
        > div:last-child { min-width: 0; text-align: right; }
    }
    ${props => props.isTotalAmount && `
        font-weight: 600;
    `}
    .stripe-active {
        color: var(--success-main);
        font-weight: 600;
    }
    .stripe-inactive {
        color: var(--info-main);
        text-align: right;
    }
    .stripe-error {
        color: var(--error-main);
        text-align: right;
    }
    .stripe-payments-stopped {
        color: #d32f2f;
        font-weight: 600;
        text-align: right;
    }
`;

export const PaymentStatusRow = styled(BillingRow)`
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;

    .MuiFormControlLabel-root {
        margin: 0;
    }

    .talent-payment-status {
        color: #4B5563;
        font-size: 0.8125rem;
        line-height: 1.5;
        width: 100%;
    }
`;

export const PaymentDetailRow = styled(BillingRow)`
    align-items: flex-start;
    gap: 0.25rem 1rem;

    > :first-child {
        flex-shrink: 0;
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

export const PaymentDetailValue = styled.div`
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    text-align: right;

    @media (max-width: 600px) {
        text-align: left;
    }
`;

export const GHRNotificationRow = styled(BillingRow)`
    color: #2e7d32;
    font-style: italic;
    font-size: 0.9rem;
    background-color: #e8f5e9;
    padding: 8px 12px;
    border-radius: 4px;
    margin-top: 0.5rem;
    
    border-left: 3px solid #2e7d32;
    
    span {
        font-weight: 600;
    }

    .amount {
        font-weight: 600;
        margin-left: 4px;
    }

    .info-icon {
        font-size: 16px;
        margin-left: 8px;
        opacity: 0.7;
        cursor: help;
    }
`;

export const TotalAmount = styled(BillingRow)`
    font-weight: 600;
    margin-top: 1rem;
    font-size: 1rem;
`;

export const EditableContent = styled.div`
    &[contenteditable="true"] {
        border-bottom: 1px dashed var(--primary-blue);
        padding: 0 4px;
        outline: none;
        min-width: 100px;
    }
`;

export const InlineEditContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    flex-wrap: wrap;
    min-width: 0;
    gap: 0.25rem;

    @media (max-width: 600px) {
        justify-content: flex-start;
    }
`;

export const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    
    h2 {
        margin: 0;
    }

    .invoice-number {
        font-size: 1rem;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.87);
    }
`;

export const CompactFormControl = styled(FormControl)`
    width: min(300px, 100%);
    margin-right: 8px;
    display: inline-flex;
    
    .MuiInputBase-root {
        background: transparent;
        font-size: inherit;
        padding: 0;
        margin: 0;
        height: auto;
        min-height: unset;
        width: 100%;
        
        input {
            padding: 0;
            border: none;
            border: 1px solid var(--primary-blue);
            background: transparent;
            height: 24px;
            line-height: 24px;
            width: 100%;
            &:focus {
                outline: none;
            }
        }
    }
    
    .MuiOutlinedInput-notchedOutline {
        border: none;
    }
    
    .MuiAutocomplete-popper {
        margin-top: 4px;
    }

    @media (max-width: 600px) {
        width: 100%;
        margin-right: 0;
    }
`;

export const StripeWarningText = styled.div`
    color: #d32f2f;
    font-size: 0.875rem;
    text-align: right;
`;
